/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Box, Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link as RouterLink } from 'react-router-dom'
import { approvalsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { formatRelativeTime } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../common/NotificationProvider'

const STATUS = { PENDING: ['Waiting', 'warning'], APPROVED: ['Approved', 'success'], REJECTED: ['Rejected', 'error'], CANCELLED: ['Cancelled', 'default'] }

/** One approval request, with approve / reject (approvers) or cancel (whoever asked). */
const ApprovalCard = ({ a, showRecord }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [note, setNote] = useState('')
  const [deciding, setDeciding] = useState(false)
  const act = useMutation({
    mutationFn: (what) => (what === 'cancel' ? approvalsAPI.cancel(a.id) : approvalsAPI[what](a.id, note.trim() || null)),
    onSuccess: (_, what) => {
      ['approvals', 'process', 'records'].forEach((k) => queryClient.invalidateQueries({ queryKey: [tenantId, k] }))
      notify({ approve: 'Approved', reject: 'Rejected', cancel: 'Cancelled' }[what])
    },
  })
  const [label, color] = STATUS[a.status]
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }}>
          {showRecord ? <RouterLink to={`/records/${a.record.entityType}/${a.record.id}`}>{a.record.title}</RouterLink> : null}
          {showRecord ? ' · ' : ''}{a.fromStage ? `${a.fromStage} → ` : ''}{a.toStage}
        </Typography>
        <Chip size="small" label={label} color={color} variant="outlined" />
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div">
        {a.ruleName} · asked by {a.requestedBy?.title || 'someone'} {formatRelativeTime(a.createdOn)}
        {a.status === 'PENDING' ? ` · approver${a.approvers.length === 1 ? '' : 's'}: ${a.approvers.map((p) => p.title).join(', ')}` : ` · ${a.decidedBy || ''}`}
      </Typography>
      {a.note && <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>“{a.note}”</Typography>}
      {a.decisionNote && <Typography variant="body2" sx={{ mt: 0.5 }} color="text.secondary">Reply: {a.decisionNote}</Typography>}
      {act.error && <Alert severity="error" sx={{ mt: 1 }}>{getErrorMessage(act.error)}</Alert>}
      {(a.canDecide || a.canCancel) && (
        <Box sx={{ mt: 1 }}>
          {deciding && <TextField size="small" fullWidth multiline placeholder="Note (needed to reject)" value={note} onChange={(e) => setNote(e.target.value)} sx={{ mb: 1 }} />}
          <Stack direction="row" spacing={1}>
            {a.canDecide && !deciding && <Button size="small" variant="contained" onClick={() => setDeciding(true)}>Decide…</Button>}
            {a.canDecide && deciding && <Button size="small" variant="contained" color="success" disabled={act.isPending} onClick={() => act.mutate('approve')}>Approve</Button>}
            {a.canDecide && deciding && <Button size="small" color="error" disabled={act.isPending || !note.trim()} onClick={() => act.mutate('reject')}>Reject</Button>}
            {a.canCancel && !a.canDecide && <Button size="small" disabled={act.isPending} onClick={() => act.mutate('cancel')}>Cancel request</Button>}
          </Stack>
        </Box>
      )}
    </Paper>
  )
}

export default ApprovalCard
